import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Brain, Heart, Droplets, X, ChevronLeft, ChevronRight, Clock, Pill, Trash2, Edit3, Lock } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { HeadacheForm, TensionForm, LloroForm } from '../components/forms';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { headachesAPI, tensionsAPI, llorosAPI } from '../services/api';
import { offlineSync } from '../services/offlineSync';
import { Headache, HeadacheStats, Tension, TensionStats, Lloro, LloroStats, HeadacheFormData, TensionFormData, LloroFormData } from '../types';
import { UpgradePrompt } from '../components/subscription/UpgradePrompt';
import { TrialBanner } from '../components/subscription/TrialBanner';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isToday
} from 'date-fns';
import { es } from 'date-fns/locale';

type ViewType = 'headaches' | 'tensions' | 'lloros';
type ModalMode = 'add' | 'view' | 'edit';

interface SelectedRecord {
  type: 'headache' | 'tension' | 'lloro';
  data: Headache | Tension | Lloro;
}

export const HomePage: React.FC = () => {
  const { user } = useAuth();
  const { canUse } = useSubscription();
  const [viewType, setViewType] = useState<ViewType>('headaches');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [headaches, setHeadaches] = useState<Headache[]>([]);
  const [tensions, setTensions] = useState<Tension[]>([]);
  const [lloros, setLloros] = useState<Lloro[]>([]);
  const [headacheStats, setHeadacheStats] = useState<HeadacheStats | null>(null);
  const [tensionStats, setTensionStats] = useState<TensionStats | null>(null);
  const [lloroStats, setLloroStats] = useState<LloroStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('add');
  const [selectedRecord, setSelectedRecord] = useState<SelectedRecord | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadData();
  }, [currentMonth]);

  const loadData = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

      // Cargar datos base (headaches siempre disponibles)
      const promises: Promise<any>[] = [
        headachesAPI.getAll({ startDate, endDate, limit: 100 }),
        canUse('tensions') ? tensionsAPI.getAll({ startDate, endDate, limit: 100 }) : Promise.resolve({ data: { tensions: [] } }),
        canUse('lloros') ? llorosAPI.getAll({ startDate, endDate, limit: 100 }) : Promise.resolve({ data: { lloros: [] } }),
        headachesAPI.getStats().catch(() => ({ data: { stats: null } })),
        canUse('tensions') ? tensionsAPI.getStats().catch(() => ({ data: { stats: null } })) : Promise.resolve({ data: { stats: null } }),
        canUse('lloros') ? llorosAPI.getStats().catch(() => ({ data: { stats: null } })) : Promise.resolve({ data: { stats: null } }),
      ];

      const [headachesRes, tensionsRes, llorosRes, hStatsRes, tStatsRes, lStatsRes] = await Promise.all(promises);
      setHeadaches(headachesRes.data?.headaches || []);
      setTensions(tensionsRes.data?.tensions || []);
      setLloros(llorosRes.data?.lloros || []);
      setHeadacheStats(hStatsRes.data?.stats || null);
      setTensionStats(tStatsRes.data?.stats || null);
      setLloroStats(lStatsRes.data?.stats || null);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = () => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  const getHeadachesForDay = (date: Date): Headache[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return headaches.filter(h => {
      // Parse the date and format it in local timezone
      const hDate = format(parseISO(h.date), 'yyyy-MM-dd');
      return hDate === dateStr;
    });
  };

  const getTensionsForDay = (date: Date): Tension[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return tensions.filter(t => {
      // Parse the date and format it in local timezone
      const tDate = format(parseISO(t.date), 'yyyy-MM-dd');
      return tDate === dateStr;
    });
  };

  const getLlorosForDay = (date: Date): Lloro[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return lloros.filter(l => {
      const lDate = format(parseISO(l.date), 'yyyy-MM-dd');
      return lDate === dateStr;
    });
  };

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);

    if (viewType === 'headaches') {
      const dayHeadaches = getHeadachesForDay(date);
      if (dayHeadaches.length > 0) {
        // Show the first record for viewing
        setSelectedRecord({ type: 'headache', data: dayHeadaches[0] });
        setModalMode('view');
        setShowForm(true);
      } else {
        // No records, open add form
        setSelectedRecord(null);
        setModalMode('add');
        setShowForm(true);
      }
    } else if (viewType === 'tensions') {
      const dayTensions = getTensionsForDay(date);
      if (dayTensions.length > 0) {
        setSelectedRecord({ type: 'tension', data: dayTensions[0] });
        setModalMode('view');
        setShowForm(true);
      } else {
        setSelectedRecord(null);
        setModalMode('add');
        setShowForm(true);
      }
    } else {
      const dayLloros = getLlorosForDay(date);
      if (dayLloros.length > 0) {
        setSelectedRecord({ type: 'lloro', data: dayLloros[0] });
        setModalMode('view');
        setShowForm(true);
      } else {
        setSelectedRecord(null);
        setModalMode('add');
        setShowForm(true);
      }
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedRecord(null);
    setModalMode('add');
    setShowDeleteConfirm(false);
  };

  const handleHeadacheSubmit = async (data: HeadacheFormData) => {
    try {
      if (modalMode === 'edit' && selectedRecord) {
        await headachesAPI.update((selectedRecord.data as Headache).id, data as any);
      } else {
        await headachesAPI.create(data as any);
      }
      closeForm();
      await loadData(true);
      return;
    } catch (error: any) {
      // Si es error de red, guardar offline
      if (!navigator.onLine || error.message === 'Failed to fetch') {
        await offlineSync.saveOperation({
          type: modalMode === 'edit' ? 'update' : 'create',
          entity: 'headache',
          data,
          entityId: modalMode === 'edit' && selectedRecord ? (selectedRecord.data as Headache).id : undefined,
        });
      } else {
        throw error;
      }
    }
    closeForm();
  };

  const handleTensionSubmit = async (data: TensionFormData) => {
    try {
      if (modalMode === 'edit' && selectedRecord) {
        await tensionsAPI.update((selectedRecord.data as Tension).id, data as any);
      } else {
        await tensionsAPI.create(data as any);
      }
      closeForm();
      await loadData(true);
      return;
    } catch (error: any) {
      if (!navigator.onLine || error.message === 'Failed to fetch') {
        await offlineSync.saveOperation({
          type: modalMode === 'edit' ? 'update' : 'create',
          entity: 'tension',
          data,
          entityId: modalMode === 'edit' && selectedRecord ? (selectedRecord.data as Tension).id : undefined,
        });
      } else {
        throw error;
      }
    }
    closeForm();
  };

  const handleLloroSubmit = async (data: LloroFormData) => {
    try {
      if (modalMode === 'edit' && selectedRecord) {
        await llorosAPI.update((selectedRecord.data as Lloro).id, data as any);
      } else {
        await llorosAPI.create(data as any);
      }
      closeForm();
      await loadData(true);
      return;
    } catch (error: any) {
      if (!navigator.onLine || error.message === 'Failed to fetch') {
        await offlineSync.saveOperation({
          type: modalMode === 'edit' ? 'update' : 'create',
          entity: 'lloro',
          data,
          entityId: modalMode === 'edit' && selectedRecord ? (selectedRecord.data as Lloro).id : undefined,
        });
      } else {
        throw error;
      }
    }
    closeForm();
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;

    try {
      if (selectedRecord.type === 'headache') {
        await headachesAPI.delete((selectedRecord.data as Headache).id);
      } else if (selectedRecord.type === 'tension') {
        await tensionsAPI.delete((selectedRecord.data as Tension).id);
      } else {
        await llorosAPI.delete((selectedRecord.data as Lloro).id);
      }
      closeForm();
      await loadData(true);
    } catch (error: any) {
      if (!navigator.onLine || error.message === 'Failed to fetch') {
        const record = selectedRecord;
        const entityId = record.type === 'headache' ? (record.data as Headache).id
          : record.type === 'tension' ? (record.data as Tension).id
          : (record.data as Lloro).id;
        await offlineSync.saveOperation({
          type: 'delete',
          entity: record.type as 'headache' | 'tension' | 'lloro',
          data: null,
          entityId,
        });
        closeForm();
      } else {
        console.error('Error deleting:', error);
      }
    }
  };

  const handleEdit = () => {
    setModalMode('edit');
  };

  const handleAddNew = () => {
    setSelectedRecord(null);
    setModalMode('add');
  };

  const weekDays = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  // Get recent records based on view type
  const recentRecords = viewType === 'headaches'
    ? headaches.slice(0, 6)
    : viewType === 'tensions'
    ? tensions.slice(0, 6)
    : lloros.slice(0, 6);

  // Current stats based on view
  const currentStats = viewType === 'headaches' ? headacheStats : tensionStats;

  // Get intensity info for headaches
  const getIntensityInfo = (intensity: string) => {
    switch (intensity) {
      case 'leve': return { emoji: '😊', bg: 'bg-green-500', text: 'text-green-500' };
      case 'moderada': return { emoji: '😐', bg: 'bg-amber-500', text: 'text-amber-500' };
      case 'fuerte': return { emoji: '😣', bg: 'bg-red-500', text: 'text-red-500' };
      default: return { emoji: '•', bg: 'bg-gray-400', text: 'text-gray-400' };
    }
  };

  // Get BP category color
  const getBPColor = (systolic: number, diastolic: number) => {
    if (systolic < 120 && diastolic < 80) return 'bg-green-500';
    if (systolic < 130 && diastolic < 80) return 'bg-yellow-500';
    if (systolic < 140 || diastolic < 90) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div>
      <Header showGreeting />

      {/* El padding inferior deja hueco para que los ultimos registros suban por
          encima del boton flotante (FAB), que ocupa hasta 9.5rem del fondo, mas el
          area segura del iPhone. Va inline porque la escala de Tailwind del proyecto
          no incluye este valor. En desktop (sin FAB) sobra hueco pero es inofensivo. */}
      <main
        className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto"
        style={{ paddingBottom: 'calc(10.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <TrialBanner />
        {/* View Type Switcher */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex p-1 mb-6 bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)]"
        >
          <button
            onClick={() => setViewType('headaches')}
            className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg font-medium text-sm sm:text-base transition-all ${
              viewType === 'headaches'
                ? 'bg-red-500 text-white shadow-sm'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            <Brain size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span>Dolores</span>
          </button>
          <button
            onClick={() => setViewType('tensions')}
            className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg font-medium text-sm sm:text-base transition-all ${
              viewType === 'tensions'
                ? 'bg-green-500 text-white shadow-sm'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
            }`}
          >
            <Heart size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span>Tensiones</span>
            {!canUse('tensions') && <Lock size={12} className="ml-1 opacity-60" />}
          </button>
          {canUse('lloros') && (
            <button
              onClick={() => setViewType('lloros')}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-2.5 sm:py-3 px-2 sm:px-4 rounded-lg font-medium text-sm sm:text-base transition-all ${
                viewType === 'lloros'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              <Droplets size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span>Lloros</span>
            </button>
          )}
        </motion.div>

        {/* Premium gate: show UpgradePrompt for locked tabs */}
        {((viewType === 'tensions' && !canUse('tensions')) || (viewType === 'lloros' && !canUse('lloros'))) ? (
          <UpgradePrompt feature={viewType} />
        ) : (
        <>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid grid-cols-3 gap-3 mb-6"
        >
          {viewType === 'headaches' ? (
            <>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">{headacheStats?.thisMonth || 0}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Este mes</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">{headacheStats?.lastMonth || 0}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Mes pasado</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">{headacheStats?.total || 0}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Total</p>
              </div>
            </>
          ) : viewType === 'tensions' ? (
            <>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">
                  {tensionStats?.avgSystolic ? Math.round(tensionStats.avgSystolic) : '-'}
                </p>
                <p className="text-xs text-[var(--color-text-muted)]">Sistólica media</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">
                  {tensionStats?.avgDiastolic ? Math.round(tensionStats.avgDiastolic) : '-'}
                </p>
                <p className="text-xs text-[var(--color-text-muted)]">Diastólica media</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">{tensionStats?.total || 0}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Total</p>
              </div>
            </>
          ) : (
            <>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">{lloroStats?.thisMonth || 0}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Este mes</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">{lloroStats?.lastMonth || 0}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Mes pasado</p>
              </div>
              <div className="card p-4 text-center">
                <p className="text-2xl font-bold text-[var(--color-text)]">{lloroStats?.total || 0}</p>
                <p className="text-xs text-[var(--color-text-muted)]">Total</p>
              </div>
            </>
          )}
        </motion.div>

        {/* Desktop: two-column layout for calendar + recent records */}
        <div className="lg:grid lg:grid-cols-5 lg:gap-8">

        {/* Calendar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-4 mb-6 lg:col-span-3 lg:mb-0"
        >
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={handlePrevMonth} className="p-2 rounded-lg hover:bg-[var(--color-bg)] transition-colors">
              <ChevronLeft size={20} className="text-[var(--color-text)]" />
            </button>
            <h2 className="text-base font-semibold text-[var(--color-text)] capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: es })}
            </h2>
            <button onClick={handleNextMonth} className="p-2 rounded-lg hover:bg-[var(--color-bg)] transition-colors">
              <ChevronRight size={20} className="text-[var(--color-text)]" />
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-xs font-medium text-[var(--color-text-muted)] py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((day) => {
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isTodayDate = isToday(day);

                const dayHeadaches = getHeadachesForDay(day);
                const dayTensions = getTensionsForDay(day);
                const dayLloros = getLlorosForDay(day);

                // Get display info based on view type
                let indicator = null;
                if (viewType === 'headaches' && dayHeadaches.length > 0) {
                  const info = getIntensityInfo(dayHeadaches[0].intensity);
                  indicator = (
                    <span className="text-xs">{info.emoji}</span>
                  );
                } else if (viewType === 'tensions' && dayTensions.length > 0) {
                  const t = dayTensions[0];
                  const colorClass = getBPColor(t.systolic, t.diastolic);
                  indicator = (
                    <div className={`w-2 h-2 rounded-full ${colorClass}`} />
                  );
                } else if (viewType === 'lloros' && dayLloros.length > 0) {
                  const lloroEmojis: Record<string, string> = {
                    leve: '😢',
                    moderado: '😭',
                    fuerte: '😩'
                  };
                  indicator = (
                    <span className="text-xs">{lloroEmojis[dayLloros[0].intensity] || '😢'}</span>
                  );
                }

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => handleDayClick(day)}
                    disabled={!isCurrentMonth}
                    className={`
                      relative min-h-[44px] lg:min-h-[52px] flex flex-col items-center justify-center rounded-lg
                      transition-all duration-150
                      ${!isCurrentMonth ? 'opacity-20 cursor-default' : 'cursor-pointer'}
                      ${isSelected ? 'bg-[var(--color-accent)] text-white ring-2 ring-[var(--color-accent)] ring-offset-2' : ''}
                      ${isTodayDate && !isSelected ? 'ring-2 ring-[var(--color-accent)]' : ''}
                      ${!isSelected && isCurrentMonth ? 'hover:bg-[var(--color-bg)]' : ''}
                      ${indicator && !isSelected ? (viewType === 'headaches' ? 'bg-red-50 dark:bg-red-900/20' : viewType === 'tensions' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-blue-50 dark:bg-blue-900/20') : ''}
                    `}
                  >
                    <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-[var(--color-text)]'}`}>
                      {format(day, 'd')}
                    </span>
                    {indicator && !isSelected && (
                      <div className="mt-0.5">
                        {indicator}
                      </div>
                    )}
                    {/* Multiple records indicator */}
                    {((viewType === 'headaches' && dayHeadaches.length > 1) ||
                      (viewType === 'tensions' && dayTensions.length > 1) ||
                      (viewType === 'lloros' && dayLloros.length > 1)) && (
                      <span className={`absolute top-0.5 right-0.5 text-[8px] font-bold ${isSelected ? 'text-white' : 'text-[var(--color-text-muted)]'}`}>
                        +{viewType === 'headaches' ? dayHeadaches.length - 1 : viewType === 'tensions' ? dayTensions.length - 1 : dayLloros.length - 1}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Add Button */}
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setSelectedDate(new Date());
              setSelectedRecord(null);
              setModalMode('add');
              setShowForm(true);
            }}
            className={`w-full mt-4 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
              viewType === 'headaches'
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : viewType === 'tensions'
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            <Plus size={20} />
            Añadir {viewType === 'headaches' ? 'dolor' : viewType === 'tensions' ? 'tensión' : 'lloro'}
          </motion.button>
        </motion.div>

        {/* Recent Records */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="lg:col-span-2"
        >
          <h2 className="text-base font-semibold text-[var(--color-text)] mb-3">
            Últimos registros
          </h2>

          {recentRecords.length === 0 ? (
            <div className="card p-6 text-center">
              <Clock size={24} className="mx-auto text-[var(--color-text-muted)] mb-2" />
              <p className="text-sm text-[var(--color-text-muted)]">Sin registros</p>
            </div>
          ) : (
            <div className="space-y-2">
              {viewType === 'headaches' ? (
                (recentRecords as Headache[]).map((h) => {
                  const info = getIntensityInfo(h.intensity);
                  return (
                    <motion.button
                      key={h.id}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setSelectedDate(parseISO(h.date));
                        setSelectedRecord({ type: 'headache', data: h });
                        setModalMode('view');
                        setShowForm(true);
                      }}
                      className="w-full card p-3 flex items-center gap-3 text-left hover:border-[var(--color-accent)] transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${
                        h.intensity === 'leve' ? 'bg-green-100 dark:bg-green-900/30' :
                        h.intensity === 'moderada' ? 'bg-amber-100 dark:bg-amber-900/30' :
                        'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        {info.emoji}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--color-text)] capitalize">{h.intensity}</p>
                        <p className="text-xs text-[var(--color-text-muted)]">
                          {format(parseISO(h.date), "d MMM, HH:mm", { locale: es })}
                          {h.location && ` · ${h.location}`}
                        </p>
                      </div>
                      {!!h.medication_taken && <Pill size={16} className="text-green-500" />}
                    </motion.button>
                  );
                })
              ) : viewType === 'tensions' ? (
                (recentRecords as Tension[]).map((t) => (
                  <motion.button
                    key={t.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedDate(parseISO(t.date));
                      setSelectedRecord({ type: 'tension', data: t });
                      setModalMode('view');
                      setShowForm(true);
                    }}
                    className="w-full card p-3 flex items-center gap-3 text-left hover:border-[var(--color-accent)] transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100 dark:bg-green-900/30">
                      <Heart size={20} className="text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--color-text)]">
                        {t.systolic}/{t.diastolic} mmHg
                        {t.pulse && <span className="text-[var(--color-text-muted)] font-normal"> · {t.pulse} bpm</span>}
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)]">
                        {format(parseISO(t.date), "d MMM", { locale: es })}, {t.time?.substring(0, 5) || '00:00'}
                      </p>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${getBPColor(t.systolic, t.diastolic)}`} />
                  </motion.button>
                ))
              ) : (
                (recentRecords as Lloro[]).map((l) => {
                  const lloroEmojis: Record<string, string> = {
                    leve: '😢',
                    moderado: '😭',
                    fuerte: '😩'
                  };
                  return (
                    <motion.button
                      key={l.id}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setSelectedDate(parseISO(l.date));
                        setSelectedRecord({ type: 'lloro', data: l });
                        setModalMode('view');
                        setShowForm(true);
                      }}
                      className="w-full card p-3 flex items-center gap-3 text-left hover:border-[var(--color-accent)] transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${
                        l.intensity === 'leve' ? 'bg-green-100 dark:bg-green-900/30' :
                        l.intensity === 'moderado' ? 'bg-amber-100 dark:bg-amber-900/30' :
                        'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        {lloroEmojis[l.intensity] || '😢'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--color-text)] capitalize">{l.intensity}</p>
                        <p className="text-xs text-[var(--color-text-muted)]">
                          {format(parseISO(l.date), "d MMM, HH:mm", { locale: es })}
                          {l.trigger_cause && ` · ${l.trigger_cause}`}
                        </p>
                      </div>
                      {l.duration && <span className="text-xs text-[var(--color-text-muted)]">{l.duration}</span>}
                    </motion.button>
                  );
                })
              )}
            </div>
          )}
        </motion.div>

        </div>{/* end desktop two-column grid */}
        </>
        )}
      </main>

      {/* Modal */}
      <AnimatePresence>
        {showForm && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
              onClick={closeForm}
            />

            {/* Mobile: modal centrado y flotante (antes era una hoja inferior) */}
            <div className="lg:hidden fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="w-full max-w-md rounded-3xl shadow-2xl overflow-hidden pointer-events-auto max-h-[88vh]"
              style={{ background: 'var(--color-surface)' }}
            >
              <div className="px-5 pt-5 overflow-y-auto overflow-x-hidden max-h-[88vh]" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.25rem))' }}>
                {/* View Mode */}
                {modalMode === 'view' && selectedRecord && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-[var(--color-text)]">
                        {selectedRecord.type === 'headache' ? 'Dolor de cabeza' : selectedRecord.type === 'tension' ? 'Tensión arterial' : 'Lloro'}
                      </h3>
                      <button onClick={closeForm} className="p-2 rounded-xl hover:bg-[var(--color-bg)]">
                        <X size={24} className="text-[var(--color-text-muted)]" />
                      </button>
                    </div>

                    {selectedRecord.type === 'headache' ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl ${
                            (selectedRecord.data as Headache).intensity === 'leve' ? 'bg-green-100 dark:bg-green-900/30' :
                            (selectedRecord.data as Headache).intensity === 'moderada' ? 'bg-amber-100 dark:bg-amber-900/30' :
                            'bg-red-100 dark:bg-red-900/30'
                          }`}>
                            {getIntensityInfo((selectedRecord.data as Headache).intensity).emoji}
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-[var(--color-text)] capitalize">
                              {(selectedRecord.data as Headache).intensity}
                            </p>
                            <p className="text-sm text-[var(--color-text-muted)]">
                              {format(parseISO((selectedRecord.data as Headache).date), "d 'de' MMMM, HH:mm", { locale: es })}
                            </p>
                          </div>
                        </div>

                        {(selectedRecord.data as Headache).location && (
                          <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                            <p className="text-xs text-[var(--color-text-muted)] mb-1">Localización</p>
                            <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Headache).location}</p>
                          </div>
                        )}

                        {!!(selectedRecord.data as Headache).medication_taken && (
                          <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/20">
                            <div className="flex items-center gap-2">
                              <Pill size={16} className="text-green-500" />
                              <p className="text-sm text-[var(--color-text)]">
                                {(selectedRecord.data as Headache).medication || 'Medicación tomada'}
                                {(selectedRecord.data as Headache).medication_time && (
                                  <span className="text-[var(--color-text-muted)]"> ({(selectedRecord.data as Headache).medication_time!.substring(0, 5)})</span>
                                )}
                              </p>
                            </div>
                          </div>
                        )}

                        {(selectedRecord.data as Headache).notes && (
                          <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                            <p className="text-xs text-[var(--color-text-muted)] mb-1">Notas</p>
                            <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Headache).notes}</p>
                          </div>
                        )}
                      </div>
                    ) : selectedRecord.type === 'tension' ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-green-100 dark:bg-green-900/30">
                            <Heart size={32} className="text-green-500" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-[var(--color-text)]">
                              {(selectedRecord.data as Tension).systolic}/{(selectedRecord.data as Tension).diastolic}
                            </p>
                            <p className="text-sm text-[var(--color-text-muted)]">
                              {format(parseISO((selectedRecord.data as Tension).date), "d 'de' MMMM", { locale: es })}, {(selectedRecord.data as Tension).time?.substring(0, 5) || '00:00'}
                            </p>
                          </div>
                        </div>

                        {(selectedRecord.data as Tension).pulse && (
                          <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                            <p className="text-xs text-[var(--color-text-muted)] mb-1">Pulso</p>
                            <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Tension).pulse} bpm</p>
                          </div>
                        )}

                        {(selectedRecord.data as Tension).notes && (
                          <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                            <p className="text-xs text-[var(--color-text-muted)] mb-1">Notas</p>
                            <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Tension).notes}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl ${
                            (selectedRecord.data as Lloro).intensity === 'leve' ? 'bg-green-100 dark:bg-green-900/30' :
                            (selectedRecord.data as Lloro).intensity === 'moderado' ? 'bg-amber-100 dark:bg-amber-900/30' :
                            'bg-red-100 dark:bg-red-900/30'
                          }`}>
                            {(selectedRecord.data as Lloro).intensity === 'leve' ? '😢' : (selectedRecord.data as Lloro).intensity === 'moderado' ? '😭' : '😩'}
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-[var(--color-text)] capitalize">
                              {(selectedRecord.data as Lloro).intensity}
                            </p>
                            <p className="text-sm text-[var(--color-text-muted)]">
                              {format(parseISO((selectedRecord.data as Lloro).date), "d 'de' MMMM, HH:mm", { locale: es })}
                            </p>
                          </div>
                        </div>

                        {(selectedRecord.data as Lloro).trigger_cause && (
                          <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                            <p className="text-xs text-[var(--color-text-muted)] mb-1">Causa</p>
                            <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Lloro).trigger_cause}</p>
                          </div>
                        )}

                        {(selectedRecord.data as Lloro).duration && (
                          <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                            <p className="text-xs text-[var(--color-text-muted)] mb-1">Duración</p>
                            <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Lloro).duration}</p>
                          </div>
                        )}

                        {(selectedRecord.data as Lloro).notes && (
                          <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                            <p className="text-xs text-[var(--color-text-muted)] mb-1">Notas</p>
                            <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Lloro).notes}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={handleEdit}
                        className="flex-1 py-3 rounded-xl bg-[var(--color-bg)] text-[var(--color-text)] font-medium flex items-center justify-center gap-2"
                      >
                        <Edit3 size={18} />
                        Editar
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="py-3 px-4 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-500 font-medium"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>

                    <button
                      onClick={handleAddNew}
                      className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                        viewType === 'headaches' ? 'bg-red-500 text-white' : viewType === 'tensions' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                      }`}
                    >
                      <Plus size={18} />
                      Añadir otro
                    </button>

                    {/* Delete Confirmation */}
                    {showDeleteConfirm && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                      >
                        <p className="text-sm text-[var(--color-text)] mb-3">¿Eliminar este registro?</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowDeleteConfirm(false)}
                            className="flex-1 py-2 rounded-lg bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={handleDelete}
                            className="flex-1 py-2 rounded-lg bg-red-500 text-white text-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Add/Edit Mode */}
                {(modalMode === 'add' || modalMode === 'edit') && (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-semibold text-[var(--color-text)]">
                        {modalMode === 'edit' ? 'Editar' : 'Nuevo'} {viewType === 'headaches' ? 'dolor de cabeza' : viewType === 'tensions' ? 'tensión arterial' : 'lloro'}
                      </h3>
                      <button onClick={closeForm} className="p-2 rounded-xl hover:bg-[var(--color-bg)]">
                        <X size={24} className="text-[var(--color-text-muted)]" />
                      </button>
                    </div>

                    {viewType === 'headaches' ? (
                      <HeadacheForm
                        onSubmit={handleHeadacheSubmit}
                        onCancel={closeForm}
                        initialDate={selectedDate || undefined}
                        initialData={modalMode === 'edit' && selectedRecord ? {
                          date: (selectedRecord.data as Headache).date,
                          intensity: (selectedRecord.data as Headache).intensity,
                          location: (selectedRecord.data as Headache).location,
                          medication_taken: (selectedRecord.data as Headache).medication_taken,
                          medication: (selectedRecord.data as Headache).medication,
                          medication_time: (selectedRecord.data as Headache).medication_time,
                          notes: (selectedRecord.data as Headache).notes,
                        } : undefined}
                      />
                    ) : viewType === 'tensions' ? (
                      <TensionForm
                        onSubmit={handleTensionSubmit}
                        onCancel={closeForm}
                        initialDate={selectedDate || undefined}
                        initialData={modalMode === 'edit' && selectedRecord ? {
                          date: `${(selectedRecord.data as Tension).date}T${(selectedRecord.data as Tension).time || '00:00'}`,
                          systolic: (selectedRecord.data as Tension).systolic,
                          diastolic: (selectedRecord.data as Tension).diastolic,
                          pulse: (selectedRecord.data as Tension).pulse,
                          notes: (selectedRecord.data as Tension).notes,
                        } : undefined}
                      />
                    ) : (
                      <LloroForm
                        onSubmit={handleLloroSubmit}
                        onCancel={closeForm}
                        initialDate={selectedDate || undefined}
                        initialData={modalMode === 'edit' && selectedRecord ? {
                          intensity: (selectedRecord.data as Lloro).intensity,
                          duration: (selectedRecord.data as Lloro).duration,
                          trigger_cause: (selectedRecord.data as Lloro).trigger_cause,
                          notes: (selectedRecord.data as Lloro).notes,
                        } : undefined}
                      />
                    )}
                  </>
                )}
              </div>
            </motion.div>
            </div>

            {/* Desktop: Centered modal */}
            <div className="hidden lg:flex fixed inset-0 z-[101] items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="w-full max-w-lg rounded-2xl shadow-2xl pointer-events-auto"
                style={{ background: 'var(--color-surface)' }}
              >
                <div className="p-6 overflow-y-auto max-h-[85vh]">
                  {/* View Mode */}
                  {modalMode === 'view' && selectedRecord && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-[var(--color-text)]">
                          {selectedRecord.type === 'headache' ? 'Dolor de cabeza' : selectedRecord.type === 'tension' ? 'Tensión arterial' : 'Lloro'}
                        </h3>
                        <button onClick={closeForm} className="p-2 rounded-xl hover:bg-[var(--color-bg)]">
                          <X size={24} className="text-[var(--color-text-muted)]" />
                        </button>
                      </div>

                      {selectedRecord.type === 'headache' ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl ${
                              (selectedRecord.data as Headache).intensity === 'leve' ? 'bg-green-100 dark:bg-green-900/30' :
                              (selectedRecord.data as Headache).intensity === 'moderada' ? 'bg-amber-100 dark:bg-amber-900/30' :
                              'bg-red-100 dark:bg-red-900/30'
                            }`}>
                              {getIntensityInfo((selectedRecord.data as Headache).intensity).emoji}
                            </div>
                            <div>
                              <p className="text-lg font-semibold text-[var(--color-text)] capitalize">
                                {(selectedRecord.data as Headache).intensity}
                              </p>
                              <p className="text-sm text-[var(--color-text-muted)]">
                                {format(parseISO((selectedRecord.data as Headache).date), "d 'de' MMMM, HH:mm", { locale: es })}
                              </p>
                            </div>
                          </div>

                          {(selectedRecord.data as Headache).location && (
                            <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                              <p className="text-xs text-[var(--color-text-muted)] mb-1">Localización</p>
                              <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Headache).location}</p>
                            </div>
                          )}

                          {!!(selectedRecord.data as Headache).medication_taken && (
                            <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/20">
                              <div className="flex items-center gap-2">
                                <Pill size={16} className="text-green-500" />
                                <p className="text-sm text-[var(--color-text)]">
                                  {(selectedRecord.data as Headache).medication || 'Medicación tomada'}
                                  {(selectedRecord.data as Headache).medication_time && (
                                    <span className="text-[var(--color-text-muted)]"> ({(selectedRecord.data as Headache).medication_time!.substring(0, 5)})</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          )}

                          {(selectedRecord.data as Headache).notes && (
                            <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                              <p className="text-xs text-[var(--color-text-muted)] mb-1">Notas</p>
                              <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Headache).notes}</p>
                            </div>
                          )}
                        </div>
                      ) : selectedRecord.type === 'tension' ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-green-100 dark:bg-green-900/30">
                              <Heart size={32} className="text-green-500" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-[var(--color-text)]">
                                {(selectedRecord.data as Tension).systolic}/{(selectedRecord.data as Tension).diastolic}
                              </p>
                              <p className="text-sm text-[var(--color-text-muted)]">
                                {format(parseISO((selectedRecord.data as Tension).date), "d 'de' MMMM", { locale: es })}, {(selectedRecord.data as Tension).time?.substring(0, 5) || '00:00'}
                              </p>
                            </div>
                          </div>

                          {(selectedRecord.data as Tension).pulse && (
                            <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                              <p className="text-xs text-[var(--color-text-muted)] mb-1">Pulso</p>
                              <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Tension).pulse} bpm</p>
                            </div>
                          )}

                          {(selectedRecord.data as Tension).notes && (
                            <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                              <p className="text-xs text-[var(--color-text-muted)] mb-1">Notas</p>
                              <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Tension).notes}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl ${
                              (selectedRecord.data as Lloro).intensity === 'leve' ? 'bg-green-100 dark:bg-green-900/30' :
                              (selectedRecord.data as Lloro).intensity === 'moderado' ? 'bg-amber-100 dark:bg-amber-900/30' :
                              'bg-red-100 dark:bg-red-900/30'
                            }`}>
                              {(selectedRecord.data as Lloro).intensity === 'leve' ? '😢' : (selectedRecord.data as Lloro).intensity === 'moderado' ? '😭' : '😩'}
                            </div>
                            <div>
                              <p className="text-lg font-semibold text-[var(--color-text)] capitalize">
                                {(selectedRecord.data as Lloro).intensity}
                              </p>
                              <p className="text-sm text-[var(--color-text-muted)]">
                                {format(parseISO((selectedRecord.data as Lloro).date), "d 'de' MMMM, HH:mm", { locale: es })}
                              </p>
                            </div>
                          </div>

                          {(selectedRecord.data as Lloro).trigger_cause && (
                            <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                              <p className="text-xs text-[var(--color-text-muted)] mb-1">Causa</p>
                              <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Lloro).trigger_cause}</p>
                            </div>
                          )}

                          {(selectedRecord.data as Lloro).duration && (
                            <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                              <p className="text-xs text-[var(--color-text-muted)] mb-1">Duración</p>
                              <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Lloro).duration}</p>
                            </div>
                          )}

                          {(selectedRecord.data as Lloro).notes && (
                            <div className="p-3 rounded-xl bg-[var(--color-bg)]">
                              <p className="text-xs text-[var(--color-text-muted)] mb-1">Notas</p>
                              <p className="text-sm text-[var(--color-text)]">{(selectedRecord.data as Lloro).notes}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-3 pt-4">
                        <button
                          onClick={handleEdit}
                          className="flex-1 py-3 rounded-xl bg-[var(--color-bg)] text-[var(--color-text)] font-medium flex items-center justify-center gap-2"
                        >
                          <Edit3 size={18} />
                          Editar
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="py-3 px-4 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-500 font-medium"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <button
                        onClick={handleAddNew}
                        className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                          viewType === 'headaches' ? 'bg-red-500 text-white' : viewType === 'tensions' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                        }`}
                      >
                        <Plus size={18} />
                        Añadir otro
                      </button>

                      {/* Delete Confirmation */}
                      {showDeleteConfirm && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                        >
                          <p className="text-sm text-[var(--color-text)] mb-3">¿Eliminar este registro?</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowDeleteConfirm(false)}
                              className="flex-1 py-2 rounded-lg bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={handleDelete}
                              className="flex-1 py-2 rounded-lg bg-red-500 text-white text-sm"
                            >
                              Eliminar
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* Add/Edit Mode */}
                  {(modalMode === 'add' || modalMode === 'edit') && (
                    <>
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-semibold text-[var(--color-text)]">
                          {modalMode === 'edit' ? 'Editar' : 'Nuevo'} {viewType === 'headaches' ? 'dolor de cabeza' : viewType === 'tensions' ? 'tensión arterial' : 'lloro'}
                        </h3>
                        <button onClick={closeForm} className="p-2 rounded-xl hover:bg-[var(--color-bg)]">
                          <X size={24} className="text-[var(--color-text-muted)]" />
                        </button>
                      </div>

                      {viewType === 'headaches' ? (
                        <HeadacheForm
                          onSubmit={handleHeadacheSubmit}
                          onCancel={closeForm}
                          initialDate={selectedDate || undefined}
                          initialData={modalMode === 'edit' && selectedRecord ? {
                            date: (selectedRecord.data as Headache).date,
                            intensity: (selectedRecord.data as Headache).intensity,
                            location: (selectedRecord.data as Headache).location,
                            medication_taken: (selectedRecord.data as Headache).medication_taken,
                            medication: (selectedRecord.data as Headache).medication,
                            medication_time: (selectedRecord.data as Headache).medication_time,
                            notes: (selectedRecord.data as Headache).notes,
                          } : undefined}
                        />
                      ) : viewType === 'tensions' ? (
                        <TensionForm
                          onSubmit={handleTensionSubmit}
                          onCancel={closeForm}
                          initialDate={selectedDate || undefined}
                          initialData={modalMode === 'edit' && selectedRecord ? {
                            date: `${(selectedRecord.data as Tension).date}T${(selectedRecord.data as Tension).time || '00:00'}`,
                            systolic: (selectedRecord.data as Tension).systolic,
                            diastolic: (selectedRecord.data as Tension).diastolic,
                            pulse: (selectedRecord.data as Tension).pulse,
                            notes: (selectedRecord.data as Tension).notes,
                          } : undefined}
                        />
                      ) : (
                        <LloroForm
                          onSubmit={handleLloroSubmit}
                          onCancel={closeForm}
                          initialDate={selectedDate || undefined}
                          initialData={modalMode === 'edit' && selectedRecord ? {
                            intensity: (selectedRecord.data as Lloro).intensity,
                            duration: (selectedRecord.data as Lloro).duration,
                            trigger_cause: (selectedRecord.data as Lloro).trigger_cause,
                            notes: (selectedRecord.data as Lloro).notes,
                          } : undefined}
                        />
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Floating Action Button - Mobile */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setSelectedDate(new Date());
          setSelectedRecord(null);
          setModalMode('add');
          setShowForm(true);
        }}
        className={`fab lg:hidden ${viewType === 'headaches' ? 'bg-red-500' : viewType === 'tensions' ? 'bg-green-500' : 'bg-blue-500'}`}
      >
        <Plus size={28} />
      </motion.button>
    </div>
  );
};
